import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getHttpClient, useHttpClient } from '@bonusly/http-client';
import { meQueryKey } from '@bonusly/who-am-i';

import { CompanyUserListSchema, TeamRequestListSchema, TeamRequestSchema } from '../schemas/team-requests';

enum QueryKey {
	TeamRequests = 'team-requests',
	MyTeamRequests = 'team-requests:mine',
	PendingConfirmations = 'team-requests:pending-confirmations',
	TeamSettings = 'team-requests:settings',
}

const headers = { 'Key-Inflection': 'camel' };

export const teamRequestsQueryKey = (params?: Record<string, unknown>) => [QueryKey.TeamRequests, params].filter(Boolean);
export const myTeamRequestsQueryKey = () => [QueryKey.MyTeamRequests];
export const pendingConfirmationsQueryKey = () => [QueryKey.PendingConfirmations];
const teamRequestsPath = () => '/team_requests';

// Admin: list pending requests
const createFetchTeamRequests = (params?: { state?: string; page?: number; per_page?: number }) => async () => {
	const http = await getHttpClient();
	const res = await http.get(teamRequestsPath(), { params, headers });
	return TeamRequestListSchema.parse(res.data);
};

export const useTeamRequests = (params?: { state?: string; page?: number; per_page?: number }) => {
	return useQuery({
		queryKey: teamRequestsQueryKey(params),
		queryFn: createFetchTeamRequests(params),
	});
};

// Manager: list my own requests
const createFetchMyTeamRequests = () => async () => {
	const http = await getHttpClient();
	const res = await http.get(`${teamRequestsPath()}/mine`, { headers });
	return TeamRequestListSchema.parse(res.data);
};

export const useMyTeamRequests = ({ enabled = true }: { enabled?: boolean } = {}) => {
	return useQuery({
		queryKey: myTeamRequestsQueryKey(),
		queryFn: createFetchMyTeamRequests(),
		enabled,
	});
};

// Manager: list requests awaiting my confirmation as new manager
const createFetchPendingConfirmations = () => async () => {
	const http = await getHttpClient();
	const res = await http.get(`${teamRequestsPath()}/pending_confirmations`, { headers });
	return TeamRequestListSchema.parse(res.data);
};

export const usePendingConfirmations = ({ enabled = true }: { enabled?: boolean } = {}) => {
	return useQuery({
		queryKey: pendingConfirmationsQueryKey(),
		queryFn: createFetchPendingConfirmations(),
		enabled,
	});
};

// Mutations

type CreateTeamRequestPayload = {
	changes: Array<{
		changeType: 'add_report' | 'remove_report' | 'change_manager';
		targetUserId?: string;
		newManagerId?: string;
	}>;
};

export const useCreateTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: CreateTeamRequestPayload) => {
			const res = await http.post(teamRequestsPath(), { teamRequest: payload }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: pendingConfirmationsQueryKey() });
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
			if (data.state === 'approved') {
				queryClient.invalidateQueries({ queryKey: ['direct-reports'] });
				queryClient.invalidateQueries({ queryKey: meQueryKey });
			}
		},
	});
};

export const useApproveTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/approve`, {}, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: ['direct-reports'] });
			queryClient.invalidateQueries({ queryKey: meQueryKey });
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};

export const useDeclineTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/decline`, { notes }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};

export const useConfirmTeamRequestChange = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, changeId, confirmed }: { id: string; changeId: string; confirmed: boolean }) => {
			const res = await http.patch(`${teamRequestsPath()}/${id}/confirm`, { changeId, confirmed }, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: pendingConfirmationsQueryKey() });
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
		},
	});
};

export const useAdminAssignTeamChange = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: { targetUserId: string; changeType: 'add_report' | 'remove_report' | 'change_manager'; newManagerId?: string }) => {
			const res = await http.post(`${teamRequestsPath()}/admin_assign`, payload, { headers });
			return TeamRequestSchema.parse(res.data);
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() }),
				queryClient.invalidateQueries({ queryKey: adminUsersQueryKey() }),
			]);
		},
	});
};

export type CompanyUsersFilter = 'all' | 'managers' | 'without_manager' | 'bizy_enabled' | 'bizy_disabled';
export type CompanyUsersRole = 'all' | 'admin' | 'employee';

type CompanyUsersParams = {
	page?: number;
	per_page?: number;
	search?: string;
	filter?: CompanyUsersFilter;
	department?: string;
	role?: CompanyUsersRole;
};

const createFetchAdminUsers = (params?: CompanyUsersParams) => async () => {
	const http = await getHttpClient();
	const cleanParams = params ? Object.fromEntries(Object.entries(params).filter(([, v]) => v != null && v !== '' && v !== 'all')) : undefined;
	const res = await http.get('/admin/users', { params: cleanParams, headers });
	return CompanyUserListSchema.parse(res.data);
};

export const adminUsersQueryKey = (params?: Record<string, unknown>) => ['admin-users', params].filter(Boolean);

export const useAdminUsers = (params?: CompanyUsersParams) => {
	return useQuery({
		queryKey: adminUsersQueryKey(params as Record<string, unknown>),
		queryFn: createFetchAdminUsers(params),
		keepPreviousData: true,
	});
};

// Team settings (auto-approve)

type TeamSettingsResponse = { autoApprove: boolean };

const teamSettingsQueryKey = () => [QueryKey.TeamSettings];

const createFetchTeamSettings = () => async () => {
	const http = await getHttpClient();
	const res = await http.get(`${teamRequestsPath()}/settings`, { headers });
	return res.data as TeamSettingsResponse;
};

export const useTeamSettings = () => {
	return useQuery({
		queryKey: teamSettingsQueryKey(),
		queryFn: createFetchTeamSettings(),
	});
};

export const useUpdateTeamSettings = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (payload: { autoApprove: boolean }) => {
			const res = await http.patch(`${teamRequestsPath()}/settings`, { auto_approve: payload.autoApprove }, { headers });
			return res.data as TeamSettingsResponse;
		},
		onMutate: async (payload) => {
			await queryClient.cancelQueries({ queryKey: teamSettingsQueryKey() });
			queryClient.setQueryData(teamSettingsQueryKey(), payload);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: teamSettingsQueryKey() });
		},
	});
};

export const useCancelTeamRequest = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (id: string) => {
			await http.delete(`${teamRequestsPath()}/${id}`, { headers });
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: myTeamRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: pendingConfirmationsQueryKey() });
			queryClient.invalidateQueries({ queryKey: teamRequestsQueryKey() });
		},
	});
};
